import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { setLocale } from "@/lib/locale";
import { applyAnalyticsGate } from "@/lib/analyticsGate";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [billing, setBilling] = useState(null);

  const refreshBilling = useCallback(async () => {
    try {
      const { data } = await api.get("/billing/status");
      setBilling(data);
      return data;
    } catch (err) {
      setBilling(null);
      return null;
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      // Non-throwing session probe — returns {authenticated, user} with 200 even
      // when not logged in, so the browser console doesn't flag boot as an error.
      const { data } = await api.get("/auth/session");
      if (data?.authenticated && data.user) {
        setUser(data.user);
        // Fetch billing state alongside so the UI can gate write actions.
        refreshBilling();
        // Route analytics: opt-out admins / lifetime-free accounts and
        // identify real users so PostHog sees stable people, not devices.
        applyAnalyticsGate(data.user);
        return data.user;
      }
      setUser(false);
      setBilling(null);
      // No user — reset any identity so PostHog doesn't merge sessions.
      try { window.posthog && window.posthog.reset && window.posthog.reset(); } catch (_) { /* noop */ }
      return null;
    } catch (err) {
      console.error("Auth refresh failed:", err);
      setUser(false);
      setBilling(null);
      return null;
    }
  }, [refreshBilling]);

  useEffect(() => {
    (async () => {
      // Load app config (currency / timezone / locale) once at boot
      try {
        const { data } = await api.get("/config");
        setLocale(data);
      } catch (err) {
        // Non-fatal: locale defaults (Australia/AUD/en-AU) kick in.
        console.warn("Locale config unavailable — using defaults:", err?.message);
      }
      await refresh();
      setLoading(false);
    })();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    refreshBilling();
    applyAnalyticsGate(data);
    return data;
  }, [refreshBilling]);

  const register = useCallback(async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    setUser(data);
    refreshBilling();
    applyAnalyticsGate(data);
    return data;
  }, [refreshBilling]);

  const signup = useCallback(async (name, email, password) => {
    const { data } = await api.post("/auth/signup", { name, email, password });
    setUser(data);
    refreshBilling();
    applyAnalyticsGate(data);
    return data;
  }, [refreshBilling]);

  const updateProfile = useCallback(async (patch) => {
    const { data } = await api.patch("/auth/me", patch);
    setUser(data);
    applyAnalyticsGate(data);
    return data;
  }, []);

  const updateOnboarding = useCallback(async (patch) => {
    // First Light setup guide — advance step or mark complete.
    const { data } = await api.patch("/auth/onboarding", patch);
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.error("Logout request failed:", err);
    }
    setUser(false);
    setBilling(null);
    // Reset PostHog identity so the next signed-in user isn't merged
    // with the previous one on the same device.
    try { window.posthog && window.posthog.reset && window.posthog.reset(); } catch (_) { /* noop */ }
  }, []);

  const value = useMemo(
    () => ({ user, loading, billing, login, register, signup, updateProfile, updateOnboarding, logout, refresh, refreshBilling, formatApiError }),
    [user, loading, billing, login, register, signup, updateProfile, updateOnboarding, logout, refresh, refreshBilling]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
